import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox, DateTimePicker, Label } from '@knack/asterisk-react';

import { type DateTimeField, type EquationField } from '@/types/schema/fields';
import { type KnackCriteriaDateTimeValue } from '@/types/schema/KnackCriteria';
import { useCriteriaHelpers } from '@/hooks/helpers/useCriteriaHelpers';
import {
  getLuxonDateFormatFromFieldFormat,
  getLuxonTimeFormatFromFieldFormat
} from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import { isDate } from '@/components/knack-date-picker/transformers/JsDateToKnackDateTransformer';
import {
  getInitialIsAnyDate,
  getInitialIsAnyTime
} from '@/components/views/form/inputs/date-time/dateTimeUtils';
import { dateTimeCriteriaValueTransformer } from './DateTimeCriteriaValueTransformer';

interface FilterDateTimeInputProps {
  field: DateTimeField | EquationField;
  criteriaIndex: number;
}

export function FilterDateTimeInput({ field, criteriaIndex }: FilterDateTimeInputProps) {
  const [t] = useTranslation();

  const formFieldName = `rules.${criteriaIndex}.value` as const;

  const {
    formState: { errors },
    setValue,
    getValues
  } = useFormContext();
  const { getDefaultCriteriaValue } = useCriteriaHelpers();

  const defaultCriteriaDateTimeValue = getDefaultCriteriaValue(field) as KnackCriteriaDateTimeValue;

  const selectedDateTimeValue: KnackCriteriaDateTimeValue = getValues(
    formFieldName
  ) as KnackCriteriaDateTimeValue;
  const datePickerValue = dateTimeCriteriaValueTransformer.toDate(
    selectedDateTimeValue || defaultCriteriaDateTimeValue,
    field.format
  );

  const [isAnyDate, setIsAnyDate] = useState(() => getInitialIsAnyDate(selectedDateTimeValue));
  const [isAnyTime, setIsAnyTime] = useState(() => getInitialIsAnyTime(selectedDateTimeValue));

  const isDateActive = field.format.date_format !== 'Ignore Date' && !isAnyDate;
  const isTimeActive = field.format.time_format !== 'Ignore Time' && !isAnyTime;

  // If the field doesn't ignore time and date, it means that the user can select 'any date' or 'any time' for each part of the date/time input
  const hasAnyDateAnyTimeOptions =
    field.format.time_format &&
    field.format.time_format !== 'Ignore Time' &&
    field.format.date_format &&
    field.format.date_format !== 'Ignore Date';

  const isDateInputActive =
    isDateActive || (!hasAnyDateAnyTimeOptions && field.format.time_format === 'Ignore Time');
  const isTimeInputActive =
    isTimeActive || (!hasAnyDateAnyTimeOptions && field.format.date_format === 'Ignore Date');

  const dateFormat = {
    date: isDateInputActive
      ? getLuxonDateFormatFromFieldFormat(field.format.date_format)
      : undefined,
    time: isTimeInputActive
      ? getLuxonTimeFormatFromFieldFormat(field.format.time_format)
      : undefined
  };

  // This effect ensures that, if the criteria `value` is not set, it uses the default criteria value for date/time fields.
  // This is necessary for scenarios where the criteria is initialized with an operator that doesn't require a value (e.g. 'is blank').
  // Example: loading a page with an existing filter in the URL params that doesn't have a value set for the criteria: {"field": "field_1", "operator": "is blank" },
  // then the user changes the operator to one that requires a value (e.g. 'is'). If the `value` is not properly set in the form, the date/time picker will break.
  useEffect(() => {
    if (!selectedDateTimeValue) {
      setValue(formFieldName, defaultCriteriaDateTimeValue);
    }
  }, [defaultCriteriaDateTimeValue, formFieldName, selectedDateTimeValue, setValue]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex-1 *:w-full">
        {(isDateInputActive || isTimeInputActive) && (
          <DateTimePicker
            intent={errors[formFieldName] ? 'destructive' : 'default'}
            key={datePickerValue?.toString()}
            format={dateFormat}
            mode="single"
            value={datePickerValue}
            onChange={(newDateValue) => {
              if (!newDateValue || !isDate(newDateValue)) {
                return;
              }

              const updatedValue = dateTimeCriteriaValueTransformer.fromDate(
                newDateValue,
                field.format,
                isDateInputActive,
                isTimeInputActive
              );

              setValue(formFieldName, { ...getValues(formFieldName), ...updatedValue });
            }}
          />
        )}

        {hasAnyDateAnyTimeOptions && (
          <div className="flex w-[220px] gap-2">
            <Label
              className="mt-2 flex items-center text-emphasis"
              htmlFor={`any-date-checkbox-${formFieldName}`}
            >
              <Checkbox
                id={`any-date-checkbox-${formFieldName}`}
                className="mr-2"
                checked={isAnyDate}
                onCheckedChange={(isChecked: boolean) => {
                  setIsAnyDate(isChecked);

                  // Reset the date value when checked
                  if (isChecked) {
                    setValue(formFieldName, {
                      ...getValues(formFieldName),
                      date: ''
                    });

                    return;
                  }

                  // Set the date value to the default date when unchecked
                  setValue(formFieldName, {
                    ...getValues(formFieldName),
                    date: defaultCriteriaDateTimeValue.date
                  });
                }}
              />
              {t('date_picker.any_date')}
            </Label>
            <Label
              className="mt-2 flex items-center text-emphasis"
              htmlFor={`any-time-checkbox-${formFieldName}`}
            >
              <Checkbox
                id={`any-time-checkbox-${formFieldName}`}
                className="mr-2"
                checked={isAnyTime}
                onCheckedChange={(isChecked: boolean) => {
                  setIsAnyTime(isChecked);

                  // Reset the time values when checked
                  if (isChecked) {
                    setValue(formFieldName, {
                      ...getValues(formFieldName),
                      time: '',
                      hours: '',
                      minutes: '',
                      am_pm: ''
                    });

                    return;
                  }

                  // Set the time values to the default time when unchecked
                  setValue(formFieldName, {
                    ...getValues(formFieldName),
                    time: defaultCriteriaDateTimeValue.time,
                    hours: defaultCriteriaDateTimeValue.hours,
                    minutes: defaultCriteriaDateTimeValue.minutes,
                    am_pm: defaultCriteriaDateTimeValue.am_pm
                  });
                }}
              />
              {t('date_picker.any_time')}
            </Label>
          </div>
        )}
      </div>
    </div>
  );
}
